import React, {useState, useMemo, ChangeEvent, useEffect} from 'react'


type SortConfig = {key: string; direction: 'asc' | 'desc' | null}

interface APITableProps {
  columns: string[]
  data: Record<string, any>[]
  searchTerm: string
  handleSort: (key: string) => void
  sortConfig: SortConfig
  handleNextClick?: (page: number) => void
  handlePriviousClick?: (page: number) => void
  onSearch: (term: string) => void
  pageSize?: number
  buttonConfig?: {
    label: string
    onClick: () => void
  }
  renderCell?: (column: string, row: Record<string, any>, rowIndex: number) => React.ReactNode
  conditionalClass?: string
  showCheckbox?: boolean
  RightSideElement?: JSX.Element
  LeftSideElement?: JSX.Element
}

const APITable: React.FC<APITableProps> = ({
  columns,
  data,
  searchTerm,
  handleNextClick,
  handlePriviousClick,
  onSearch,
  pageSize = 10,
  buttonConfig,
  renderCell,
  conditionalClass,
  showCheckbox = false,
  RightSideElement,
  LeftSideElement,
  handleSort,
  sortConfig,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [paginatedData, setPaginatedData] = useState<any[]>([])
  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    onSearch(event.target.value)
  }

  const filteredData = useMemo(() => {
    return data.filter((row) =>
      Object.values(row).join(' ').toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [data, searchTerm])

  const handleCheckboxChange = (index: number) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(index)
        ? prevSelected.filter((row) => row !== index)
        : [...prevSelected, index]
    )
  }

  const handleSelectAll = () => {
    if (selectedRows.length === data.length) {
      setSelectedRows([])
    } else {
      setSelectedRows(data.map((_, index) => index))
    }
  }


  useEffect(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    setPaginatedData(filteredData.slice(startIndex, endIndex))
  }, [filteredData, currentPage, pageSize])

  return (
    <>
      <div className='flex-lg-row-fluid'>
        <div className={conditionalClass}>
          <div className='card-header pt-3'>
            <div
              className='card-title'
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '-7px',
                  justifyContent: 'flex-start',
                  width: '100%',
                }}
              >
                <i className='ki-duotone ki-magnifier fs-1 position-absolute ms-6'>
                  <span className='path1'></span>
                  <span className='path2'></span>
                </i>
                <input
                  type='text'
                  value={searchTerm}
                  onChange={handleSearchChange}
                  data-kt-roles-table-filter='search'
                  className='form-control form-control-solid w-250px ps-15'
                  placeholder='Search Users'
                />

                     {LeftSideElement &&
                  <div style={{ marginLeft: '10px',height: '100%', display: 'flex', alignItems: 'center',width: '100%' }}>
                      {LeftSideElement}
                  </div>}
              </div>

              {/* Right-aligned "Hello" */}
              {/* <div style={{width: '100%', display: 'flex', justifyContent: 'flex-end'}}>
                {RightSideElement && <div>{RightSideElement}</div>}
              </div> */}
            </div>
          </div>
          <div className='card-body pt-0' style={{ overflowX: 'auto' }}>
            <div style={{ overflowX: 'auto' }}>
              <table className='table table-striped align-middle fs-6 gy-5 mb-0'>
                <thead>
                  <tr className='text-start fw-bold fs-7 gs-0'>
                    {/* {showCheckbox && (
                      <th className='w-30px pe-2'>
                        <div className='form-check form-check-sm form-check-custom form-check-solid me-3'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            onChange={() => handleSelectAll()}
                            checked={selectedRows.length === data.length}
                          />
                        </div>
                      </th>
                    )} */}
                    {columns.map((column, index) => (
                      <th
                        key={index}
                        onClick={() => handleSort(column)}
                        style={{
                          width: `${100 / columns.length}%`, // Distribute width equally
                          minWidth: '120px', // Prevent columns from becoming too narrow
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                          position: 'relative',
                        }}
                      >
                        {column}
                        <span style={{ marginLeft: '8px', display: 'inline-block' }}>
                          {sortConfig.key === column ? (
                             sortConfig.direction ==='desc' ? (
                              <span
                              style={{
                                marginLeft: '4px',
                                color: 'blue',
                              }}
                            >
                             ↑
                            </span>
                             ):(
                              <span
                            style={{
                              marginLeft: '4px',
                              color: 'blue',
                            }}
                          >
                            ↓
                          </span>
                             )
                          ):(
                            // Show both arrows for unsorted
                            <>
                             <i className="ki-solid ki-arrow-up-down"></i>
                            </>
                          )}
                      
                         
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                {filteredData.length === 0 ? (
                  <tbody>
                    <tr>
                      <td colSpan={columns.length} style={{ textAlign: 'center'}}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#555' }}>
                          <i className='fas fa-search' style={{ fontSize: '36px', color: '#ccc' }}></i>
                          <span style={{ marginTop: '10px', fontSize: '16px', fontWeight: '500' }}>
                            No data found
                          </span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody className='fw-semibold text-gray-600'>
                    {paginatedData.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {showCheckbox && (
                          <td>
                            <div className='form-check form-check-sm form-check-custom form-check-solid me-3'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={selectedRows.includes(rowIndex)}
                                onChange={() => handleCheckboxChange(rowIndex)}
                              />
                            </div>
                          </td>
                        )}
                        {columns.map((column) => {
                          const cellValue = row[column];
                          // const isNumber = typeof cellValue === 'number';

                          return (
                            <td
                              key={column}
                              style={{
                                width: `${100 / columns.length}%`, // Adjust dynamically
                                minWidth: '120px',
                                whiteSpace: 'nowrap',
                                textAlign: 'left',
                              }}
                            >
                              {renderCell ? renderCell(column, row, rowIndex) : cellValue}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default APITable
